import api from './api';

/**
 * Reusable contactinfo + notifications sub-table. Contacts are keyed by
 * (tablename, tablerecno) so the same UI serves locations, customers, etc.
 */

export interface ContactType {
  recno: number;
  type: string;
}

export interface Contact {
  recno: number;
  tablename: string;
  tablerecno: number;
  contacttype: number;
  contactname: string;
  contacttitle: string;
  contactphone: string;
  contactfax: string;
  contactemail: string;
  communication: string;
  notes: string;
  deleted: string;
}

export interface NotificationRow {
  recno?: number;
  notificationtype: string;
  contactinforecno?: number;
  email: string;
  checked: string;
  csr: string;
  whsecsr: string;
  dispatcher: string;
}

/** Types seeded for a new contact (legacy @notificationtypes order). */
export const DEFAULT_NOTIFICATION_TYPES = [
  'DROPPED', 'IN PER DIEM', 'PER DIEM INVOICE ESTIMATE', 'EXPORT RECEIVAL',
  'ARRIVAL', 'ARRIVAL - SUMMARY', 'SHIPMENT', 'SHIPMENT - SUMMARY', 'OUTGATE',
  'DOC CUTOFF', 'STORAGE LFD', 'EXPORT CONTAINER UPDATE', 'WAREHOUSE CARGO TRANSFER',
  'GWIMOVE - BOL', 'GWIMOVE - EIR', 'GWIMOVE - POD', 'WAITING TIME',
];

// Seed defaults for a brand-new contact's notifications.
const CHECKED_BY_DEFAULT = new Set([
  'DROPPED', 'IN PER DIEM', 'PER DIEM INVOICE ESTIMATE', 'EXPORT RECEIVAL',
  'ARRIVAL', 'SHIPMENT', 'OUTGATE', 'DOC CUTOFF', 'STORAGE LFD',
]);
const WHSECSR_BY_DEFAULT = new Set(['ARRIVAL', 'SHIPMENT', 'STORAGE LFD']);

/** Default notification rows for a new contact (with seeded On / Whse CSR flags). */
export function defaultNotifications(): NotificationRow[] {
  return DEFAULT_NOTIFICATION_TYPES.map((t) => ({
    notificationtype: t,
    email: '',
    checked: CHECKED_BY_DEFAULT.has(t) ? 'Y' : 'N',
    csr: 'N',
    whsecsr: WHSECSR_BY_DEFAULT.has(t) ? 'Y' : 'N',
    dispatcher: 'N',
  }));
}

export async function getContactTypes(): Promise<ContactType[]> {
  // Deliberately NOT routed through lookupCache: the list is user-maintained
  // (Data Management → Contact Types), and the 5-min cached copy hid a newly
  // added type from the contacts drawer's Type dropdown until a page reload.
  // The table is a handful of rows — pull it fresh every time it's needed.
  const res = await api.get<{ data: ContactType[] }>('/contacts/types');
  return res.data?.data || [];
}

export async function listContacts(tablename: string, tablerecno: number): Promise<Contact[]> {
  const res = await api.get('/contacts', { params: { tablename, tablerecno } });
  return res.data?.data || [];
}

export async function getContact(recno: number): Promise<{ contact: Contact; notifications: NotificationRow[] }> {
  const res = await api.get(`/contacts/${recno}`);
  return res.data?.data;
}

export async function createContact(payload: Record<string, any>): Promise<{ recno: number }> {
  const res = await api.post('/contacts', payload);
  return res.data?.data;
}

export async function updateContact(recno: number, payload: Record<string, any>): Promise<void> {
  await api.put(`/contacts/${recno}`, payload);
}

export async function deleteContact(recno: number): Promise<void> {
  await api.delete(`/contacts/${recno}`);
}
